import { fetchApi } from "~/lib/api";
import type {
  AdminBlogCommentsResponse,
  AdminGetBlogCommentsParams,
  AdminGetBlogPostsParams,
  BlogComment,
  BlogPageStats,
  BlogPost,
  BlogPostInsert,
  BlogPostWithCommentCount,
  GetBlogPageStatsParams,
  PaginatedQueryParams,
  Pagination,
} from "~/types";
import { serializeFilterParams } from "./utils";


export const adminBlogService = {
  /**
   * Get all blog posts
   */
  async getPosts(
    options: AdminGetBlogPostsParams = {},
  ): Promise<Pagination<BlogPostWithCommentCount>> {
    const query = serializeFilterParams(options);
    const endpoint = `/admin/blog${query}`;
    return fetchApi<Pagination<BlogPostWithCommentCount>>(endpoint);
  },

  /**
   * Get a single blog post by ID
   */
  async getPost(id: number): Promise<BlogPost> {
    return fetchApi<BlogPost>(`/admin/blog/${id}`);
  },

  /**
   * Create a new blog post
   */
  async createPost(data: BlogPostInsert): Promise<BlogPost> {
    return fetchApi<BlogPost>("/admin/blog", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Generate a blog post draft via Copilot and return the sessionId
   */
  async generatePost(seed?: string): Promise<{ sessionId: string }> {
    return fetchApi<{ sessionId: string }>("/admin/blog/generate", {
      method: "POST",
      body: JSON.stringify({ seed }),
    });
  },

  /**
   * Update a blog post
   */
  async updatePost(id: number, data: Partial<BlogPost>): Promise<BlogPost> {
    return fetchApi<BlogPost>(`/admin/blog/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a blog post (soft delete)
   */
  async deletePost(id: number): Promise<{ message: string }> {
    return fetchApi<{ message: string }>(`/admin/blog/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Restore a deleted blog post
   */
  async restorePost(id: number): Promise<{ message: string; post: BlogPost }> {
    return fetchApi<{ message: string; post: BlogPost }>(
      `/admin/blog/${id}/restore`,
      {
        method: "POST",
      },
    );
  },

  /**
   * Permanently delete a blog post (hard delete)
   */
  async permanentDeletePost(
    id: number,
  ): Promise<{ message: string; id: number }> {
    return fetchApi<{ message: string; id: number }>(
      `/admin/blog/${id}/permanent`,
      {
        method: "DELETE",
      },
    );
  },

  /**
   * Publish/unpublish a blog post
   */
  async togglePublish(id: number) {
    return fetchApi<BlogPost>(`/admin/blog/${id}/toggle-publish`, {
      method: "POST",
    });
  },

  /**
   * Get all blog comments for moderation
   */
  async getAllComments(options: AdminGetBlogCommentsParams = {}) {
    const query = serializeFilterParams(options);
    const endpoint = `/admin/blog/comments/all${query}`;
    return fetchApi<AdminBlogCommentsResponse>(endpoint);
  },

  /**
   * Approve or reject a comment
   */
  async moderateComment(commentId: number, approved: boolean) {
    return fetchApi<BlogComment>(`/admin/blog/comments/${commentId}/moderate`, {
      method: "PATCH",
      body: JSON.stringify({ approved }),
    });
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: number) {
    return fetchApi<{ message: string }>(`/admin/blog/comments/${commentId}`, {
      method: "DELETE",
    });
  },
};

export const adminBlogAnalyticsService = {
  /**
   * Get Umami page stats for a single blog post URL.
   */
  async getPageStats(params: GetBlogPageStatsParams): Promise<BlogPageStats> {
    const query = serializeFilterParams(params);
    return fetchApi<BlogPageStats>(`/admin/analytics/blog/stats${query}`);
  },
};

export const anonymousBlogService = {
  /**
   * Get approved comments for a blog post
   */
  async getComments(
    blogPostId: number,
    options: PaginatedQueryParams = {},
  ): Promise<Pagination<BlogComment>> {
    const query = serializeFilterParams(options);
    const endpoint = `/anonymous/blog/comments/${blogPostId}${query}`;
    return fetchApi<Pagination<BlogComment>>(endpoint);
  },

  /**
   * Create a new comment
   */
  async createComment(
    blogPostId: number,
    data: {
      authorName: string;
      authorEmail?: string;
      content: string;
    },
  ): Promise<BlogComment> {
    return fetchApi<BlogComment>(`/anonymous/blog/comments/${blogPostId}`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  /**
   * Get all published blog posts
   */
  async getPosts(options: PaginatedQueryParams = {}, fetchOptions: RequestInit = {}): Promise<BlogPost[]> {
    const query = serializeFilterParams(options);
    const endpoint = `/anonymous/blog/posts${query}`;
    return fetchApi<BlogPost[]>(endpoint, {
      cache: "force-cache",
      ...fetchOptions,
    });
  },

  async getPost(slug: string): Promise<BlogPost | null> {
    return fetchApi<BlogPost>(`/anonymous/blog/posts/${slug}`, {
      cache: "force-cache",
    });
  },

  /**
   * Get related blog posts by slug
   */
  async getRelatedPosts(slug: string, limit: number = 3): Promise<BlogPost[]> {
    return fetchApi<BlogPost[]>(
      `/anonymous/blog/posts/${slug}/related?limit=${limit}`,
      { cache: "force-cache" },
    );
  },
};
